import { Box, Button, Stack, Typography } from "@mui/material";
import { useStoriesQuery } from "Api/Queries/Stories/useStoriesQuery";
import { useStoryPostUsageQuery } from "Api/Queries/Stories/useStoryPostUsageQuery";
import { useGetContextsQuery } from "Auth/Api/Queries/Context/useGetContextsQuery";
import { ErrorText } from "Components/Shared/Errors/ErrorText";
import { StoryActionButtons } from "Components/Stories/Management/StoryActionButtons";
import { StoryCardGrid } from "Components/Stories/Management/StoryCardGrid";
import type { StoryListHandlers } from "Components/Stories/Management/storyListColumns";
import { SortDirection } from "Infrastructure/Api/Api";
import { useState } from "react";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Stories.Management;

const GRID_PAGE_SIZE = 24;

/** The Merchant list: the author's own stories as a phone-friendly card grid with "load more". */
export const MerchantStoriesGrid = ({
  isPending,
  onPreview,
  onPublish,
  onUnpublish,
  onDelete,
}: StoryListHandlers) => {
  const { t } = useResource();
  const [pageCount, setPageCount] = useState(1);

  const { data: contexts } = useGetContextsQuery();
  const { data: usage } = useStoryPostUsageQuery(
    contexts?.activeIdentityUserPublicID,
  );

  // Offset paging grown in place ("load more"): the merchant's own list is small, and
  // keepPreviousData keeps the already-shown cards on screen while the next page loads.
  const query = useStoriesQuery({
    offset: 0,
    limit: pageCount * GRID_PAGE_SIZE,
    sortBy: "dateCreated",
    sortDirection: SortDirection.Descending,
  });

  if (query.isError) {
    return <ErrorText>{t(RESOURCES.LoadError)}</ErrorText>;
  }

  const stories = query.data?.items ?? [];
  const hasMore = (query.data?.totalCount ?? 0) > stories.length;

  return (
    <Stack gap={2}>
      {usage && (
        <Typography variant="body2" color="text.secondary">
          {t(Resources.Stories.PostStory.UsageLabel, {
            used: usage.used,
            limit: usage.limit,
          })}
        </Typography>
      )}
      <StoryCardGrid
        stories={stories}
        isLoading={query.isLoading}
        isAuthorVisible={false}
        emptyText={t(RESOURCES.Empty)}
        onMediaClick={onPreview}
        renderActions={(story) => (
          <StoryActionButtons
            story={story}
            isPending={isPending}
            onPublish={onPublish}
            onUnpublish={onUnpublish}
            onDelete={onDelete}
          />
        )}
      />
      {hasMore && (
        <Box alignSelf="center">
          <Button
            variant="outlined"
            disabled={query.isFetching}
            onClick={() => setPageCount((count) => count + 1)}
          >
            {t(Resources.Common.LoadMore)}
          </Button>
        </Box>
      )}
    </Stack>
  );
};
